import { OutlineElement } from '@phase2/outline-core';
import { CSSResultArray, html, TemplateResult } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import componentStyles from './pc-disclaimer.css.lit';
import '../pc-button/pc-button';

@customElement('pc-disclaimer')
export class PCDisclaimer extends OutlineElement {
  static styles: CSSResultArray = [componentStyles];
  // Query the dialog element for easy reference
  @query('dialog') dialog!: HTMLDialogElement;

  // Store the href attribute of the clicked mailto link
  href: string | null = null;
  // text to the translate context
  @property({ type: String, attribute: 'text-cancel' })
  textCancel: string;
  @property({ type: String, attribute: 'text-ok' })
  textOk: string;

  // Handle click events on slots, specifically looking for mailto links

  handleLinkClick(event: Event): void {
    // Traverse up through the event path to find the anchor tag
    let target = event.target as HTMLElement;
    let anchor = null;

    // Safety counter to prevent excessive looping (unlikely but safe)
    let safetyCounter = 0;
    const MAX_ITERATIONS = 5; // Reasonable limit for DOM traversal depth

    // Check if target itself is an anchor
    if (
      target &&
      target.tagName === 'A' &&
      target.getAttribute('href')?.startsWith('mailto:')
    ) {
      anchor = target;
    } else if (target) {
      // If not, look for an anchor parent within the disclaimer component
      while (target && target !== this && safetyCounter < MAX_ITERATIONS) {
        safetyCounter++;
        if (target.tagName === 'A') {
          const href = target.getAttribute('href');
          if (href && href.startsWith('mailto:')) {
            anchor = target;
            break;
          }
        }
        const parentElement = target.parentElement;
        if (!parentElement) break; // Extra safety check if parentElement is null
        target = parentElement as HTMLElement;
      }
    }

    // If we found a mailto link, handle it
    if (anchor) {
      // Prevent the default mail client from opening
      event.preventDefault();
      // Store the href to use after the disclaimer is accepted
      this.href = anchor.getAttribute('href');
      this.showModalDialog();
    }
  }

  proceedToLink(): void {
    if (this.href) {
      // Redirect to the mailto link
      // and Close the disclaimer dialog

      window.location.href = this.href;
      this.closeModal();
    }
  }
  // Show the modal dialog and prevent the main document from scrolling
  showModalDialog(): void {
    if (this.dialog) {
      this.dialog.showModal();
      document.body.style.overflow = 'hidden';
    }
  }
  // Close the modal dialog and allow the main document to scroll again
  closeModal(): void {
    if (this.dialog) {
      this.dialog.close();
      document.body.style.overflow = '';
    }
  }

  render(): TemplateResult {
    return html`
      <slot @click="${this.handleLinkClick}"></slot>
      <dialog id="disclaimerDialog" @close="${this.closeModal}">
        <slot name="dialog-content"></slot>
        <div class="cto-container">
          <pc-button
            @click="${this.closeModal}"
            aria-label="Close dialog"
            variant="secondary"
            >${this.textCancel ?? 'Cancel'}</pc-button
          >
          <pc-button
            @click="${this.proceedToLink}"
            aria-label="Proceed to email client"
            variant="primary"
            >${this.textOk ?? 'Ok'}</pc-button
          >
        </div>
      </dialog>
    `;
  }
  disconnectedCallback(): void {
    // Ensure body overflow is restored if component is removed while dialog is open
    super.disconnectedCallback();
    document.body.style.overflow = '';
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-disclaimer': PCDisclaimer;
  }
}
